import styled from 'styled-components';
import { memo, useEffect, useMemo, useRef, useState, useCallback } from 'react';

import {
  ParticipantPosition,
  ParticipantStatus,
  TeamType,
} from '@typings/participant';
import { useCoHostData } from '@redux/selectors/participants';
import { useSpeakerDataById } from '@redux/selectors/speakers';
import { useHostData } from '@redux/selectors/participants';
import { useCurrentUser } from '@redux/selectors/users';
import {
  PARTICIPANT_NAME_HEIGHT,
  PARTICIPANT_SKEW_OFFSET,
} from '@utils/constants';
import { isEmptyObject } from '@utils/index';
import { useAppDispatch } from '@redux/store';
import { likesSlice } from '@redux/state/likesSlice';

import socket from '../../../services/socket.service';

import HostView from './components/HostView';
import SpeakerView from './components/SpeakerView';

const MAX_WIDTH = 1200;
const HOST_HEIGHT_PERCENTAGE = 0.48;
const SPEAKER_HEIGHT_PERCENTAGE = 0.48;

const Container = styled.div`
  width: 100%;
  max-width: ${MAX_WIDTH}px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: center;
  justify-content: flex-start;
  padding-block: 8px;
`;

const HostsContainer = styled.div.attrs<{ $width: number; $height: number }>(
  ({ $width, $height }) => ({
    style: {
      width: `${$width * 2 + Math.ceil($height * PARTICIPANT_SKEW_OFFSET)}px`,
    },
  }),
)<{ $width: number; $height: number }>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0;
  z-index: 2;
`;

const SpeakersContainer = styled.div.attrs<{ $width: number; $height: number }>(
  ({ $width, $height }) => ({
    style: {
      width: `${$width * 2 + Math.ceil($height * PARTICIPANT_SKEW_OFFSET)}px`,
    },
  }),
)<{ $width: number; $height: number }>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0;
  margin-top: 0;
`;

const Participants = ({
  showId,
  speakingUIDs,
  isStatusesShown,
  isRecord,
  leftSpeakerId,
  rightSpeakerId,
}: {
  showId: string;
  speakingUIDs: Record<string, number>;
  isStatusesShown: boolean;
  isRecord: boolean;
  leftSpeakerId: string | null;
  rightSpeakerId: string | null;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });

  const hostHeight = useMemo(
    () =>
      Math.ceil(
        containerSize.height * HOST_HEIGHT_PERCENTAGE - PARTICIPANT_NAME_HEIGHT,
      ),
    [containerSize.height],
  );
  const speakerHeight = useMemo(
    () =>
      Math.ceil(
        containerSize.height * SPEAKER_HEIGHT_PERCENTAGE -
          PARTICIPANT_NAME_HEIGHT,
      ),
    [containerSize.height],
  );

  const hostWidth = useMemo(
    () =>
      Math.floor(
        (containerSize.width -
          Math.ceil(hostHeight * PARTICIPANT_SKEW_OFFSET)) /
          2,
      ),
    [containerSize.width, hostHeight],
  );
  const speakerWidth = useMemo(
    () =>
      Math.floor(
        (containerSize.width -
          Math.ceil(speakerHeight * PARTICIPANT_SKEW_OFFSET)) /
          2,
      ),
    [containerSize.width, speakerHeight],
  );

  const currentUser = useCurrentUser();
  const hostData = useHostData(showId);
  const isHostOnRight = useMemo(
    () => hostData?.team === TeamType.Red,
    [hostData?.team],
  );
  const coHostData = useCoHostData(showId);
  const leftSpeakerProfileId = useSpeakerDataById(leftSpeakerId)?.profileId;
  const rightSpeakerProfileId = useSpeakerDataById(rightSpeakerId)?.profileId;
  const amISpeaker = useMemo(
    () =>
      !!currentUser?.id &&
      (currentUser?.id === leftSpeakerProfileId ||
        currentUser?.id === rightSpeakerProfileId),
    [currentUser?.id, leftSpeakerProfileId, rightSpeakerProfileId],
  );

  const isShowCoHost = useMemo(
    () =>
      isRecord
        ? coHostData?.status !== ParticipantStatus.Pending
        : coHostData?.status !== ParticipantStatus.Pending &&
          coHostData?.status !== ParticipantStatus.Left,
    [isRecord, coHostData?.status],
  );

  const likesBuffer = useRef<{
    likesByUser: Record<string, { count: number }>;
    likesBySpeaker: Record<string, { count: number }>;
  }>({ likesByUser: {}, likesBySpeaker: {} });
  const dispatch = useAppDispatch();
  const renderHost = useCallback(
    () => (
      <HostView
        key={hostData?.id}
        participantId={hostData?.id}
        position={
          isHostOnRight ? ParticipantPosition.Right : ParticipantPosition.Left
        }
        side={hostData?.team || TeamType.Neutral}
        size={{ height: hostHeight, width: hostWidth }}
        speakingUIDs={speakingUIDs}
        isStatusesShown={isStatusesShown}
      />
    ),
    [
      hostData?.id,
      hostData?.team,
      isHostOnRight,
      hostHeight,
      hostWidth,
      speakingUIDs,
      isStatusesShown,
    ],
  );

  const renderCoHost = useCallback(
    () =>
      isShowCoHost && coHostData?.id ? (
        <HostView
          key={coHostData?.id}
          participantId={coHostData?.id}
          position={
            isHostOnRight ? ParticipantPosition.Left : ParticipantPosition.Right
          }
          side={coHostData?.team || TeamType.Neutral}
          size={{ height: hostHeight, width: hostWidth }}
          speakingUIDs={speakingUIDs}
          isStatusesShown={isStatusesShown}
        />
      ) : (
        <div />
      ),
    [
      isShowCoHost,
      coHostData?.id,
      coHostData?.team,
      isHostOnRight,
      hostHeight,
      hostWidth,
      speakingUIDs,
      isStatusesShown,
    ],
  );

  const renderHosts = useCallback(() => {
    return isHostOnRight ? (
      <>
        {renderCoHost()}
        {renderHost()}
      </>
    ) : (
      <>
        {renderHost()}
        {renderCoHost()}
      </>
    );
  }, [isHostOnRight, renderHost, renderCoHost]);

  useEffect(() => {
    const updateContainerSize = () => {
      if (containerRef.current) {
        const newWidth =
          containerRef.current.offsetWidth < MAX_WIDTH
            ? containerRef.current.offsetWidth
            : MAX_WIDTH;
        const newHeight = containerRef.current.offsetHeight;

        setContainerSize((prevSize) => {
          if (newWidth !== prevSize.width || newHeight !== prevSize.height) {
            return {
              width: newWidth,
              height: newHeight,
            };
          }
          return prevSize;
        });
      }
    };

    updateContainerSize();
    window.addEventListener('resize', updateContainerSize);

    return () => {
      window.removeEventListener('resize', updateContainerSize);
    };
  }, []);

  useEffect(() => {
    if (isRecord) {
      return;
    }

    socket.on(
      `likes-show-${showId}`,
      (payload: {
        userId?: string;
        speakerId?: string;
        count: number;
        likedById: string;
      }) => {
        const { userId, speakerId, count, likedById } = payload;
        if (likedById === currentUser?.id) {
          return;
        }

        if (userId) {
          likesBuffer.current.likesByUser[userId] = { count };
        }

        if (speakerId) {
          likesBuffer.current.likesBySpeaker[speakerId] = { count };
        }
      },
    );

    const interval = setInterval(() => {
      if (!isEmptyObject(likesBuffer.current.likesByUser)) {
        dispatch(
          likesSlice.actions.setLikesByUser({
            showId,
            likesByUser: likesBuffer.current.likesByUser,
          }),
        );
        likesBuffer.current.likesByUser = {};
      }
      if (!isEmptyObject(likesBuffer.current.likesBySpeaker)) {
        dispatch(
          likesSlice.actions.setLikesBySpeaker({
            showId,
            likesBySpeaker: likesBuffer.current.likesBySpeaker,
          }),
        );
        likesBuffer.current.likesBySpeaker = {};
      }
    }, 2000);

    return () => {
      clearInterval(interval);
      socket.off(`likes-show-${showId}`);
    };
  }, [isRecord, showId, currentUser?.id, dispatch]);

  return (
    <Container ref={containerRef}>
      <HostsContainer $width={hostWidth} $height={hostHeight}>
        {renderHosts()}
      </HostsContainer>

      {!leftSpeakerId && !rightSpeakerId ? null : (
        <SpeakersContainer $width={speakerWidth} $height={speakerHeight}>
          <SpeakerView
            key={leftSpeakerId}
            speakerId={leftSpeakerId}
            position={ParticipantPosition.Left}
            size={{ height: speakerHeight, width: speakerWidth }}
            speakingUIDs={speakingUIDs}
            isRecord={isRecord}
            isStatusesShown={isStatusesShown}
            amISpeaker={amISpeaker}
          />

          <SpeakerView
            key={rightSpeakerId}
            speakerId={rightSpeakerId}
            position={ParticipantPosition.Right}
            size={{ height: speakerHeight, width: speakerWidth }}
            speakingUIDs={speakingUIDs}
            isRecord={isRecord}
            isStatusesShown={isStatusesShown}
            amISpeaker={amISpeaker}
          />
        </SpeakersContainer>
      )}
    </Container>
  );
};

export default memo(Participants);
